/**
 * Citizen-comprehension content as compact cards: summary, who's for and
 * against it, what a YES/NO vote means, and fiscal impact. The Ballot
 * Initiatives report card renders these (bare); the measure detail page has
 * its own section layouts (MeasureReaderSections, MeasureSidesSection) built
 * on the same fields. PRD §5.6.7 requires the provenance labels wherever the
 * content renders.
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import RichContent from '../../RichContent';
import { richTextIsEmpty } from '../../../utils/richText';
import { OpenExternalIcon } from '../../icons';
import { SectionCard, ProvenancedText, ProvenanceNote } from './detailAtoms';
import type { MeasureContentSource } from '../types';

const FISCAL_DETAIL_LABELS: Record<string, string> = {
  state_revenue: 'State revenue',
  state_spending: 'State spending',
  local_impact: 'Local impact',
  tabor_refund: 'TABOR refund',
  fiscal_years: 'Fiscal years',
  notes: 'Notes',
  additional_information: 'Additional information',
};

/** The reader-facing label for a fiscal_impact_detail key. */
export const fiscalDetailLabel = (key: string): string =>
  FISCAL_DETAIL_LABELS[key] || key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());

/** The fiscal breakdown entries that have something to say, in stored order. */
export const fiscalDetailEntries = (detail: Record<string, unknown> | null | undefined): Array<[string, unknown]> =>
  Object.entries(detail ?? {}).filter(([, value]) => value != null && String(value).trim() !== '');

interface MeasureContentCardsProps {
  initiative: MeasureContentSource;
  /**
   * Render inside another card (the Ballot Initiatives report card, PRD v2
   * §5.17.3): no card chrome, and no section heading where the content labels
   * itself — the citizen Ballot Card leads with the summary and lets the
   * YES/NO and sides boxes carry their own captions.
   */
  bare?: boolean;
}

export const SummaryCard: React.FC<MeasureContentCardsProps> = ({ initiative, bare = false }) => {
  const provenance = initiative.content_provenance ?? {};
  // "Show more" reveals the long description beneath the summary (PRD v2 §5.10.7);
  // hooks run before the early return so the order is stable.
  const [showLong, setShowLong] = useState(false);
  const hasLong = !richTextIsEmpty(initiative.plain_language_long_description);
  // richTextIsEmpty everywhere a card gates on a rich-text field: a
  // Quill-emptied value (`<p><br></p>`) is blank, not an empty bordered box.
  if (richTextIsEmpty(initiative.plain_language_summary) && !hasLong) return null;
  return (
    <SectionCard title={bare ? '' : 'Summary'} bare={bare}>
      {!richTextIsEmpty(initiative.plain_language_summary) && (
        <ProvenancedText
          text={initiative.plain_language_summary as string}
          provenance={provenance.plain_language_summary}
        />
      )}
      {hasLong && (
        <div className="mt-2">
          {showLong && (
            <ProvenancedText
              text={initiative.plain_language_long_description as string}
              provenance={provenance.plain_language_long_description}
            />
          )}
          <button
            type="button"
            onClick={() => setShowLong((v) => !v)}
            aria-expanded={showLong}
            className="mt-1.5 inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
          >
            {showLong ? 'Show less' : 'Show more'}
            {showLong ? <ChevronUp className="h-4 w-4" aria-hidden /> : <ChevronDown className="h-4 w-4" aria-hidden />}
          </button>
        </div>
      )}
    </SectionCard>
  );
};

/**
 * Who supports it and who opposes it, and why — staff-authored synthesis for the
 * reader (PRD v2 §5.10.7), distinct from the sourced ArgumentsCard below. Neutral
 * headers: the sides' cases are information; the vote-means row is the decision.
 */
export const SidesCard: React.FC<MeasureContentCardsProps> = ({ initiative, bare = false }) => {
  const provenance = initiative.content_provenance ?? {};
  const hasSupport = !richTextIsEmpty(initiative.who_supports_and_why);
  const hasOppose = !richTextIsEmpty(initiative.who_opposes_and_why);
  if (!hasSupport && !hasOppose) return null;
  return (
    <SectionCard title={bare ? '' : 'Who Supports It, Who Opposes It'} bare={bare}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {hasSupport && (
          <div className="rounded-lg bg-background-secondary p-3 dark:bg-background-dark-tertiary">
            <div className="text-xs font-semibold uppercase text-text-tertiary dark:text-text-dark-tertiary mb-1.5">
              Who supports it and why
            </div>
            <ProvenancedText text={initiative.who_supports_and_why as string} provenance={provenance.who_supports_and_why} />
          </div>
        )}
        {hasOppose && (
          <div className="rounded-lg bg-background-secondary p-3 dark:bg-background-dark-tertiary">
            <div className="text-xs font-semibold uppercase text-text-tertiary dark:text-text-dark-tertiary mb-1.5">
              Who opposes it and why
            </div>
            <ProvenancedText text={initiative.who_opposes_and_why as string} provenance={provenance.who_opposes_and_why} />
          </div>
        )}
      </div>
    </SectionCard>
  );
};

export const VoteMeaningCard: React.FC<MeasureContentCardsProps> = ({ initiative, bare = false }) => {
  const provenance = initiative.content_provenance ?? {};
  const hasYes = !richTextIsEmpty(initiative.what_yes_means);
  const hasNo = !richTextIsEmpty(initiative.what_no_means);
  if (!hasYes && !hasNo) return null;
  return (
    <SectionCard title={bare ? '' : 'What Your Vote Means'} bare={bare}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {hasYes && (
          <div className="rounded-lg border border-success-200 dark:border-success-800 bg-success-100/40 dark:bg-success-900/10 p-3">
            <div className="text-xs font-semibold uppercase text-success-700 dark:text-success-300 mb-1.5">
              A YES vote means
            </div>
            <ProvenancedText text={initiative.what_yes_means as string} provenance={provenance.what_yes_means} />
          </div>
        )}
        {hasNo && (
          <div className="rounded-lg border border-error-200 dark:border-error-800 bg-error-100/40 dark:bg-error-900/10 p-3">
            <div className="text-xs font-semibold uppercase text-error-700 dark:text-error-300 mb-1.5">
              A NO vote means
            </div>
            <ProvenancedText text={initiative.what_no_means as string} provenance={provenance.what_no_means} />
          </div>
        )}
      </div>
    </SectionCard>
  );
};

/**
 * Fiscal impact: the statement, the breakdown, then one provenance label — the
 * two fields are declared together in admin (v2 PRD §5.6.2), so the reader gets
 * one label, under whichever block comes last. The state's own statement is
 * linked when the measure has one; a missing URL shows nothing here.
 */
export const FiscalCard: React.FC<MeasureContentCardsProps> = ({ initiative, bare = false }) => {
  const provenance = initiative.content_provenance ?? {};
  const entries = fiscalDetailEntries(initiative.fiscal_impact_detail);
  const hasStatement = !richTextIsEmpty(initiative.fiscal_impact_statement);
  if (!hasStatement && entries.length === 0) return null;
  const fiscalProvenance = provenance.fiscal_impact_statement ?? provenance.fiscal_impact_detail;
  return (
    <SectionCard title="Fiscal Impact" bare={bare}>
      {hasStatement && (
        <div className={entries.length > 0 ? 'mb-3' : ''}>
          <ProvenancedText
            text={initiative.fiscal_impact_statement as string}
            provenance={entries.length > 0 ? undefined : fiscalProvenance}
          />
        </div>
      )}
      {entries.length > 0 && (
        <>
          <dl className="space-y-1.5">
            {entries.map(([key, value]) => (
              <div key={key} className="flex gap-2 text-sm">
                <dt className="font-medium text-text-primary dark:text-text-dark-primary shrink-0">
                  {fiscalDetailLabel(key)}:
                </dt>
                <dd className="text-text-secondary dark:text-text-dark-secondary">
                  <RichContent html={String(value)} className="text-sm text-text-secondary dark:text-text-dark-secondary" />
                </dd>
              </div>
            ))}
          </dl>
          <ProvenanceNote provenance={fiscalProvenance} />
        </>
      )}
      {initiative.fiscal_impact_url && (
        <a
          href={initiative.fiscal_impact_url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-1 text-sm text-primary-600 hover:underline dark:text-primary-400"
        >
          <OpenExternalIcon className="h-3 w-3" />
          Read the state&apos;s fiscal impact statement
        </a>
      )}
    </SectionCard>
  );
};
